/**
 * Compact chip variant of PreviewTextItem (senders).
 */

import { memo } from 'react';
import { Check } from 'lucide-react';

import { cn } from '@/lib/utils';

import {
  SMS_TEXT_MAX_LENGTH,
  isSmsTextOverLimit,
  smsEffectiveGsmLength,
} from '../smsTextLimit';

interface Props {
  text: string;
  index: number;
  isSelected: boolean;
  onToggle: (idx: number) => void;
  lengthLimit?: number;
  lengthSourceText?: string;
}

export const PreviewTextItemChip = memo(function PreviewTextItemChip({
  text,
  index,
  isSelected,
  onToggle,
  lengthLimit,
  lengthSourceText,
}: Props) {
  const sourceForLimit = lengthSourceText ?? text;
  const overLimit = lengthLimit != null && isSmsTextOverLimit(sourceForLimit);
  const limitCap = lengthLimit ?? SMS_TEXT_MAX_LENGTH;
  const effectiveLen = smsEffectiveGsmLength(sourceForLimit);
  const showDev = import.meta.env.MODE === 'development';

  const borderCls = overLimit
    ? 'border-destructive/70 bg-destructive/10 ring-1 ring-destructive/25'
    : isSelected
      ? 'border-primary/40 bg-primary/5'
      : 'border-transparent bg-muted/30';
  const cbBorder = overLimit
    ? isSelected
      ? 'border-destructive bg-destructive text-destructive-foreground'
      : 'border-destructive/50'
    : isSelected
      ? 'border-primary bg-primary text-primary-foreground'
      : 'border-muted-foreground/40';

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onToggle(index)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onToggle(index);
        }
      }}
      className={cn(
        'relative inline-flex shrink-0 cursor-pointer items-center gap-1.5 rounded-md border px-2 py-1.5 text-xs transition-colors',
        showDev && 'pb-5 pr-2',
        borderCls,
      )}
    >
      <span
        className={cn(
          'mt-0 flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-sm border',
          cbBorder,
        )}
      >
        {isSelected && <Check className="h-2.5 w-2.5" />}
      </span>
      <span
        className={cn(
          'min-w-0 max-w-[140px] truncate leading-tight',
          overLimit ? 'text-destructive' : 'text-foreground',
        )}
      >
        {text}
      </span>
      {overLimit && (
        <span className="shrink-0 rounded bg-destructive/15 px-1 py-0.5 text-[10px] font-semibold text-destructive">
          {effectiveLen}/{limitCap}
        </span>
      )}
      {showDev && (
        <span
          className="pointer-events-none absolute bottom-1 right-1.5 text-[9px] tabular-nums leading-none text-muted-foreground/80"
          aria-hidden
          title="Effective GSM-7 length (merge tokens counted as substituted width)"
        >
          {effectiveLen}
          {lengthLimit != null ? `/${limitCap}` : ''} eff.
        </span>
      )}
    </div>
  );
});
